import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

import { AuthProvider, useAuth } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";

import Landing from "@/pages/Landing";
import Features from "@/pages/Features";
import Pricing from "@/pages/Pricing";
import Login, { Signup } from "@/pages/Login";
import Onboarding from "@/pages/Onboarding";
import Dashboard from "@/pages/Dashboard";
import Projects from "@/pages/Projects";
import ScriptEditor from "@/pages/ScriptEditor";
import Characters from "@/pages/Characters";
import Scenes from "@/pages/Scenes";
import Locations from "@/pages/Locations";
import Storyboards from "@/pages/Storyboards";
import AIStudio from "@/pages/AIStudio";
import Production from "@/pages/Production";
import Assets from "@/pages/Assets";
import Relationships from "@/pages/Relationships";
import BeatBoard from "@/pages/BeatBoard";
import ContinuityPage from "@/pages/Continuity";
import StoryPublic from "@/pages/StoryPublic";
import { BillingPage, PaymentSuccess, PaymentCancel } from "@/pages/Billing";
import { Community, Settings, Docs, GenericPage } from "@/pages/Misc";

function AppRoutes() {
  const { user } = useAuth();
  return (
    <Routes>
      {/* Marketing */}
      <Route path="/" element={<Landing />} />
      <Route path="/features" element={<Features />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/ai-writer" element={<GenericPage title="AI Writer" subtitle="Feature" />} />
      <Route path="/script-doctor" element={<GenericPage title="Script Doctor" subtitle="Feature" />} />
      <Route path="/storyboard" element={<GenericPage title="Storyboard Studio" subtitle="Feature" />} />
      <Route path="/docs" element={<Docs />} />
      <Route path="/about" element={<GenericPage title="About StoryFrame AI" subtitle="Company" />} />
      <Route path="/contact" element={<GenericPage title="Contact us" subtitle="Company" />} />
      <Route path="/blog" element={<GenericPage title="Blog" subtitle="Content" />} />
      <Route path="/terms" element={<GenericPage title="Terms" subtitle="Legal" />} />
      <Route path="/privacy" element={<GenericPage title="Privacy" subtitle="Legal" />} />
      <Route path="/community" element={<Community />} />

      {/* Auth */}
      <Route path="/login" element={user ? <Navigate to="/app" replace /> : <Login />} />
      <Route path="/signup" element={user ? <Navigate to="/app" replace /> : <Signup />} />
      <Route path="/forgot-password" element={<GenericPage title="Reset password" subtitle="Auth" />} />

      {/* Onboarding */}
      <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />

      {/* App */}
      <Route path="/app" element={<ProtectedRoute requireOnboarded><Dashboard /></ProtectedRoute>} />
      <Route path="/app/projects" element={<ProtectedRoute requireOnboarded><Projects /></ProtectedRoute>} />
      <Route path="/app/projects/:projectId/script" element={<ProtectedRoute requireOnboarded><ScriptEditor /></ProtectedRoute>} />
      <Route path="/app/projects/:projectId/scenes" element={<ProtectedRoute requireOnboarded><Scenes /></ProtectedRoute>} />
      <Route path="/app/projects/:projectId/characters" element={<ProtectedRoute requireOnboarded><Characters /></ProtectedRoute>} />
      <Route path="/app/projects/:projectId/locations" element={<ProtectedRoute requireOnboarded><Locations /></ProtectedRoute>} />
      <Route path="/app/projects/:projectId/storyboards" element={<ProtectedRoute requireOnboarded><Storyboards /></ProtectedRoute>} />
      <Route path="/app/projects/:projectId/ai" element={<ProtectedRoute requireOnboarded><AIStudio /></ProtectedRoute>} />
      <Route path="/app/projects/:projectId/production" element={<ProtectedRoute requireOnboarded><Production /></ProtectedRoute>} />
      <Route path="/app/projects/:projectId/assets" element={<ProtectedRoute requireOnboarded><Assets /></ProtectedRoute>} />
      <Route path="/app/projects/:projectId/relationships" element={<ProtectedRoute requireOnboarded><Relationships /></ProtectedRoute>} />
      <Route path="/app/projects/:projectId/beats" element={<ProtectedRoute requireOnboarded><BeatBoard /></ProtectedRoute>} />
      <Route path="/app/projects/:projectId/continuity" element={<ProtectedRoute requireOnboarded><ContinuityPage /></ProtectedRoute>} />
      <Route path="/story/:slug" element={<StoryPublic />} />
      <Route path="/app/billing" element={<ProtectedRoute><BillingPage /></ProtectedRoute>} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route path="/app/community" element={<ProtectedRoute><Community /></ProtectedRoute>} />
      <Route path="/app/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

const TOAST_STYLE = {
  background: "#141414",
  color: "#FFFFF0",
  border: "1px solid #27272A",
  borderRadius: 0,
};
const TOAST_OPTIONS = { style: TOAST_STYLE };

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster theme="dark" toastOptions={TOAST_OPTIONS} />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
